import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useGasReport, GasMonthDetail } from '../../hooks/useGasReport';
import { useConsumption } from '../../contexts/ConsumptionContext';
import { createRoundedBarChart } from '../../hooks/useRoundedBarChart';

declare global {
  interface Window {
    Chart: any;
    $: any;
  }
}

/**
 * GasReportPage - Página de Relatório de Consumo de Gás
 * 
 * Pixel-perfect com a versão Rails original.
 * Endpoint: GET /consumo/gas/:id/api_data (dados iniciais)
 * Endpoint: GET /api/v1/statements/:period/details/:rgi/gas (detalhes por mês)
 */
export function GasReportPage() {
  const { id } = useParams<{ id: string }>();
  const { currentResidenceId, setCurrentResidenceId } = useConsumption();
  
  // Determina o residenceId a usar: da URL ou do contexto
  const residenceId = id || currentResidenceId;
  
  // Atualiza o contexto se a URL mudou
  useEffect(() => {
    if (id && id !== currentResidenceId) {
      setCurrentResidenceId(id);
    }
  }, [id, currentResidenceId, setCurrentResidenceId]);

  // Busca dados da API
  const { data, isLoading, error, refetch, loadMonthDetails, monthDetails, loadingMonths } = useGasReport(residenceId);

  // Não renderiza nada enquanto carrega - preloader global está visível
  if (isLoading) {
    return null;
  }

  // Mostra erro apenas se houver erro
  if (error) {
    return <GasReportError error={error} onRetry={refetch} />;
  }

  if (!data) {
    return null;
  }

  // Renderiza a página com dados reais
  return (
    <GasReportContent
      data={data}
      loadMonthDetails={loadMonthDetails}
      monthDetails={monthDetails}
      loadingMonths={loadingMonths}
    />
  );
}

/**
 * Error state component
 */
function GasReportError({ error, onRetry }: { error: string | null; onRetry: () => void }) {
  const { t } = useTranslation();
  
  return (
    <div className="w-full">
      <div 
        className="
          relative
          pl-[60px] pr-8 py-[12px]
          !bg-[#e84b3d] text-white
          rounded
          after:content-[''] after:absolute after:w-[57px] after:h-[52px]
          after:bg-[url('/images/icons-set.png')] after:bg-no-repeat
          after:top-[14px] after:left-[5px]
          after:bg-[position:11px_-226px]
        " 
        role="alert"
      >
        <h4 className="text-lg font-bold mb-2">{t('common.error', 'Erro')}</h4>
        <p className="mb-3">{error || t('views.consumption.error_loading', 'Não foi possível carregar os dados do relatório.')}</p>
        <hr className="border-white/30 my-3" />
        <button 
          className="px-4 py-2 border border-white rounded text-white hover:bg-white hover:text-[#e84b3d] transition-colors"
          onClick={onRetry}
        >
          {t('common.retry', 'Tentar novamente')}
        </button>
      </div>
    </div>
  );
}

/**
 * Content component - recebe dados já carregados
 */
interface GasReportContentProps {
  data: NonNullable<ReturnType<typeof useGasReport>['data']>;
  loadMonthDetails: (period: string, monthId: string) => Promise<GasMonthDetail | null>;
  monthDetails: Record<string, GasMonthDetail>;
  loadingMonths: Record<string, boolean>;
}

function GasReportContent({
  data,
  loadMonthDetails,
  monthDetails,
  loadingMonths,
}: GasReportContentProps) {
  const { t } = useTranslation();
  const dailyChartRef = useRef<HTMLCanvasElement>(null);
  const monthlyChartRef = useRef<HTMLCanvasElement>(null);
  const dailyChartInstanceRef = useRef<any>(null);
  const monthlyChartInstanceRef = useRef<any>(null);
  
  // Estado para controle de accordion (múltiplos podem estar abertos)
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());

  // Verifica se há dias com leituras faltando (marcados com asterisco)
  const hasMissingReadings = data.dailyDays.some(d => String(d).includes('*'));

  // Cor do gás
  const GAS_COLOR = '#f89243';

  // Carregar gráfico de consumo diário (bar chart com topo arredondado)
  useEffect(() => {
    if (!dailyChartRef.current || !window.Chart) return;

    // Destrói gráfico anterior se existir
    if (dailyChartInstanceRef.current) {
      dailyChartInstanceRef.current.destroy();
      dailyChartInstanceRef.current = null;
    }

    const captions = {
      consumption_in: t('views.general.charts.consumption_in', 'Consumo em'),
      period: t('views.general.charts.checked_days', 'Dias Aferidos'),
    };

    try {
      dailyChartInstanceRef.current = createRoundedBarChart(dailyChartRef.current, {
        labels: data.dailyDays,
        datasets: [{
          label: t('views.general.charts.type_gas', 'Gás'),
          borderColor: GAS_COLOR,
          borderWidth: 0,
          backgroundColor: GAS_COLOR,
          data: data.gasConsumption,
        }],
        tooltipUnit: 'l',
        yAxisLabel: captions.consumption_in + ' litros',
        xAxisLabel: captions.period,
        barRoundness: 1,
        maintainAspectRatio: false,
      });
    } catch (err) {
      console.error('[GasReportPage] Error creating daily chart:', err);
    }

    return () => {
      if (dailyChartInstanceRef.current) {
        dailyChartInstanceRef.current.destroy();
        dailyChartInstanceRef.current = null;
      }
    };
  }, [data, t]);

  // Carregar gráfico mensal (bar chart com topo arredondado)
  useEffect(() => {
    if (!monthlyChartRef.current || !window.Chart) return;
    
    // Destrói gráfico anterior se existir
    if (monthlyChartInstanceRef.current) {
      monthlyChartInstanceRef.current.destroy();
      monthlyChartInstanceRef.current = null;
    }

    const captions = {
      consumption_in: t('views.general.charts.consumption_in', 'Consumo em'),
      period: t('views.general.charts.checked_months', 'Meses Aferidos'),
    };

    try {
      monthlyChartInstanceRef.current = createRoundedBarChart(monthlyChartRef.current, {
        labels: data.monthlyLabels,
        datasets: [{
          label: t('views.general.charts.type_gas', 'Gás'),
          borderColor: GAS_COLOR,
          borderWidth: 0,
          backgroundColor: GAS_COLOR,
          data: data.monthlyConsumption,
        }],
        tooltipUnit: 'm³',
        yAxisLabel: captions.consumption_in + ' m³',
        xAxisLabel: captions.period,
        barRoundness: 1,
        maintainAspectRatio: false,
      });
    } catch (err) {
      console.error('[GasReportPage] Error creating monthly chart:', err);
    }

    return () => {
      if (monthlyChartInstanceRef.current) {
        monthlyChartInstanceRef.current.destroy();
        monthlyChartInstanceRef.current = null;
      }
    };
  }, [data, t]);

  // Função para toggle do accordion (múltiplos podem estar abertos)
  const handleAccordionClick = useCallback(async (monthId: string, period: string) => {
    setExpandedMonths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(monthId)) {
        newSet.delete(monthId);
      } else {
        newSet.add(monthId);
        // Carrega detalhes se não existir
        if (!monthDetails[monthId]) {
          loadMonthDetails(period, monthId);
        }
      }
      return newSet;
    });
  }, [monthDetails, loadMonthDetails]);

  return (
    <>
      {/* Container flex para os dois gráficos lado a lado */}
      <div className="flex flex-wrap w-full">
        {/* Gráfico de Consumo Diário */}
        <div className="w-full md:w-1/2 px-[15px]">
          <div className="p-4">
            <div>
              <div>
                <div>
                  <h4 className="text-[#7b8aa4] text-[18px] font-['Poppins',sans-serif]">{t('views.consumption.report.daily_consumption', 'Consumo diário do período corrente')}</h4>
                </div>
              </div>
              <div className="mt-4 bg-white rounded-[10px] relative w-full h-[200px] md:h-[240px]">
                <canvas ref={dailyChartRef} id="barchart_daily_consumption" data-color={GAS_COLOR}></canvas>
              </div>
            </div>
          </div>
          {hasMissingReadings && (
            <div className="block text-[#999] text-[12px] italic pl-4">
              {t('views.consumption.report.hint', '* Dias com leituras estimadas')}
            </div>
          )}
        </div>

        {/* Gráfico de Leituras Anteriores (Mensal) */}
        <div className="w-full md:w-1/2 px-[15px]">
          <div>
            <div className="p-4">
              <div>
                <div>
                  <div>
                    <h4 className="text-[#7b8aa4] text-[18px] font-['Poppins',sans-serif]">{t('views.consumption.report.last_readings', 'Últimas contas - Cálculo mensal')}</h4>
                  </div>
                </div>
                <div className="mt-4 bg-white rounded-[10px] relative w-full h-[200px] md:h-[240px]">
                  <canvas ref={monthlyChartRef} id="lineChart_monthly_consumption" data-color={GAS_COLOR}></canvas>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-[15px]">
        <hr className="mt-4 mb-4" />
      </div>

      {/* Accordion de Relatório Mensal */}
      <div className="w-full px-[15px]">
        <div className="p-4">
          <div>
            <div>
              <div>
                <h4 className="text-[#7b8aa4] text-[18px] font-['Poppins',sans-serif]">{t('views.consumption.report.monthly_usage_report', 'Relatório de consumo dos meses anteriores')}</h4>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <div className="space-y-2">
              {data.lastStatements.map((statement) => (
                <GasMonthAccordionItem
                  key={statement.monthId}
                  statement={statement}
                  isExpanded={expandedMonths.has(statement.monthId)}
                  isLoading={loadingMonths[statement.monthId] || false}
                  detail={monthDetails[statement.monthId]}
                  onToggle={() => handleAccordionClick(statement.monthId, statement.period)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-[15px]">
        <hr className="mt-4 mb-4" />
      </div>
    </>
  );
}

/**
 * Item do accordion para cada mês (Gás)
 */
interface GasMonthAccordionItemProps {
  statement: {
    period: string;
    periodDate: Date;
    month: string;
    monthId: string;
    reading?: number;
    consumption: number;
  };
  isExpanded: boolean;
  isLoading: boolean;
  detail?: GasMonthDetail;
  onToggle: () => void;
}

function GasMonthAccordionItem({ statement, isExpanded, isLoading, detail, onToggle }: GasMonthAccordionItemProps) {
  const { t } = useTranslation();
  const monthChartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<any>(null);

  const GAS_COLOR = '#f89243';

  // Carregar gráfico quando os detalhes são carregados
  useEffect(() => {
    if (!isExpanded || !detail || !monthChartRef.current || !window.Chart) return;

    // Destrói gráfico anterior
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }

    // Se não há dados, esconde o container e retorna
    if (detail.gasConsumption.length === 0) {
      return;
    }

    const captions = {
      consumption_in: t('views.general.charts.consumption_in', 'Consumo em'),
      period: t('views.general.charts.checked_days', 'Dias Aferidos'),
    };

    chartInstanceRef.current = createRoundedBarChart(monthChartRef.current, {
      labels: detail.days,
      datasets: [{
        label: t('views.general.charts.type_gas', 'Gás'),
        borderColor: GAS_COLOR,
        borderWidth: 0,
        backgroundColor: GAS_COLOR,
        data: detail.gasConsumption,
      }],
      tooltipUnit: 'l',
      yAxisLabel: captions.consumption_in + ' litros',
      xAxisLabel: captions.period,
      barRoundness: 1,
      maintainAspectRatio: false,
      paddingTop: 20,
    });

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [isExpanded, detail, t]);

  const hasChartData = detail && detail.gasConsumption.length > 0;

  // Mapeamento de status para classes Tailwind
  const statusClasses: Record<string, string> = {
    'abaixo': 'bg-[rgb(45,204,111)] text-white text-[11px] px-[5px] py-[3px] rounded-[2px] font-["Open_Sans",sans-serif]',
    'dentro': 'bg-[dimgray] text-white text-[11px] px-[5px] py-[3px] rounded-[2px] font-["Open_Sans",sans-serif]',
    'ultrapassou': 'bg-[#e84b3d] text-white text-[11px] px-[5px] py-[3px] rounded-[2px] font-["Open_Sans",sans-serif]',
    'danger': 'bg-[#e84b3d] text-white text-[11px] px-[5px] py-[3px] rounded-[2px] font-["Open_Sans",sans-serif]',
  };

  return (
    <div className="p-0 bg-white rounded-[5px] border-0">
      <div className="rounded-[5px] p-0">
        <button 
          className={`
            w-full text-left px-4 py-2 rounded-[5px] relative border-0 outline-none text-[16px] font-medium
            ${isExpanded 
              ? 'bg-[#797767]' 
              : 'bg-white text-[#797767]'
            }
          `}
          style={isExpanded ? { color: '#ffffff' } : undefined}
          onClick={onToggle}
          aria-expanded={isExpanded}
          aria-controls={statement.monthId}
        >
          {statement.month}
          <span 
            className={`float-right text-[12px] bg-[#797767] px-[15px] py-[2px] rounded-[10px] ${isExpanded ? 'hidden' : 'inline-block'}`}
            style={{ color: '#ffffff' }}
          >
            {t('views.consumption.report.details', 'Ver detalhes')}
          </span>
          <span 
            className={`float-right text-[12px] font-bold ${isExpanded ? 'inline-block' : 'hidden'}`}
            style={{ color: '#ffffff' }}
          >
            {t('views.consumption.report.close', 'X')}
          </span>
        </button>
      </div>
      <div 
        id={statement.monthId} 
        className={`${isExpanded ? 'block' : 'hidden'}`}
      >
        <div className="p-4 min-h-[90px]">
          {/* Spinner */}
          {isLoading && (
            <div className="flex justify-center items-center min-h-[200px]">
              <svg className="w-[50px] h-[50px] animate-spin" viewBox="25 25 50 50">
                <circle 
                  className="stroke-[#f89243]" 
                  cx="50" cy="50" r="20" 
                  fill="none" 
                  strokeWidth="2" 
                  strokeMiterlimit="10"
                  strokeDasharray="89, 200"
                  strokeDashoffset="-35"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          )}
          
          {/* Conteúdo */}
          {!isLoading && detail && (
            <div className="mt-3">
              <ul className="flex flex-wrap gap-4 ml-5 text-[15px] list-none p-0 max-md:block max-md:ml-0 max-md:text-[12px]">
                <li className="text-[#797767] flex items-start gap-1">
                  <i 
                    className="inline-block w-[22px] h-[23px] my-0 mx-[5px] bg-[url('/images/icons-set.png')] bg-no-repeat" 
                    style={{ backgroundPosition: '-46px -223px' }}
                  ></i>
                  <strong className="font-medium">{t('views.consumption.report.period', 'Período')}:</strong> {detail.beginOfPeriod} {t('views.consumption.report.to', 'a')} {detail.endOfPeriod}
                </li>
                <li className="text-[#797767] flex items-start gap-1">
                  <i 
                    className="inline-block w-[22px] h-[23px] my-0 mx-[5px] bg-[url('/images/icons-set.png')] bg-no-repeat" 
                    style={{ backgroundPosition: '-67px -222px' }}
                  ></i>
                  <strong className="font-medium">{t('views.consumption.report.daily_average', 'Média diária')}:</strong>&nbsp;
                  <span>{detail.averageConsumption} {t('views.consumption.units.liters', 'L')}</span>
                </li>
                <li className="text-[#797767] flex items-start gap-1">
                  <i 
                    className="inline-block w-[22px] h-[23px] my-0 mx-[5px] bg-[url('/images/icons-set.png')] bg-no-repeat" 
                    style={{ backgroundPosition: '-88px -222px' }}
                  ></i>
                  <span className="inline">
                    {detail.goal.base === 'goal' ? (
                      <>
                        <strong className="font-medium">{t('views.consumption.report.consumption_goal', 'Consumo em relação à meta')}:</strong>&nbsp;
                        <span className={statusClasses[detail.goal.status.toLowerCase()] || ''}>{detail.goal.status}</span>
                      </>
                    ) : (
                      <>
                        <strong className="font-medium">{t('views.consumption.report.consumption_average', 'Consumo em relação à média')}:</strong>&nbsp;
                        <span className={statusClasses[detail.goal.status.toLowerCase()] || ''}>{detail.goal.status}</span>
                      </>
                    )}
                  </span>
                </li>
                <li className="text-[#797767] flex items-start gap-1">
                  <i 
                    className="inline-block w-[22px] h-[23px] my-0 mx-[5px] bg-[url('/images/icons-set.png')] bg-no-repeat" 
                    style={{ backgroundPosition: '-110px -222px' }}
                  ></i>
                  <strong className="font-medium">{t('views.consumption.report.total_consumption', 'Consumo total')}:</strong>&nbsp;
                  <span>{Number(detail.totalConsumption).toFixed(2)} m³</span>
                </li>
              </ul>
              
              {/* Gráfico do mês */}
              {hasChartData && (
                <div className="w-full px-[15px]">
                  <div className="mt-4 bg-white rounded-[10px] relative w-full h-[280px]">
                    <canvas ref={monthChartRef} id={`${statement.monthId}_daily_consumption_chart`} data-color={GAS_COLOR}></canvas>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
